import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaLightbulb, FaRocket, FaShieldAlt, FaChartBar, FaCloud, FaVrCardboard } from 'react-icons/fa';
import styles from './TexGenia.module.css';

const TexGenia: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const features = [
    {
      icon: <FaChartBar />,
      title: 'Sensor-Driven Monitoring',
      description: 'Real-time condition monitoring for equipment'
    },
    {
      icon: <FaLightbulb />,
      title: 'Predictive Analytics',
      description: 'AI dashboards to minimize downtime'
    },
    {
      icon: <FaCloud />,
      title: 'Cloud-Based Management',
      description: 'Comprehensive asset tracking system'
    },
    {
      icon: <FaVrCardboard />,
      title: 'VR Training',
      description: 'Immersive maintenance training'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Digital Twin',
      description: 'Virtual replicas for simulation'
    },
    {
      icon: <FaRocket />,
      title: 'Federated AI',
      description: 'Distributed AI processing'
    }
  ];

  return (
    <section id="texgenia" className={styles.texgenia} ref={ref}>
      <div className={styles.container}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className={styles.header}
        >
          <h2 className={styles.title}>TexGenia</h2>
          <div className={styles.titleUnderline}></div>
        </motion.div>

        <div className={styles.content}>
          <motion.div
            className={styles.infoSection}
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className={styles.logoPlaceholder}>
              <span>TexGenia</span>
            </div>
            
            <p className={styles.description}>
              AI-enhanced CMMS for hospitals, factories, and industry.
            </p>

            <div className={styles.productBadge}>
              <span className={styles.badgeLabel}>Flagship Product</span>
              <span className={styles.badgeName}>TexCare CMMS Pro</span>
            </div>
          </motion.div>

          <motion.div
            className={styles.featuresGrid}
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className={styles.featureCard}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
              >
                <div className={styles.featureIcon}>{feature.icon}</div>
                <h4 className={styles.featureTitle}>{feature.title}</h4>
                <p className={styles.featureDescription}>{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default TexGenia;
